import styled from "@emotion/styled";
import AutoCompletePrediction from "./AutoCompletePrediction";
import Question from "./OnboardingSection/Question";
import * as Dialog from "@radix-ui/react-dialog";
import Spinner from "./Generic/Spinner";
import { AppIcon } from "./Generic/AppIcon";
import { keyframes } from "@emotion/react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import React from "react";
import { TIMES_SVG } from "../utils/svgs";
import Flex from "./Generic/Flex";

export interface BasePrediction {
  id: string;
  title: string;
  renderLeading?: () => React.ReactNode;
}

function AutoCompleteDialog<T extends BasePrediction>(props: {
  query: string;
  isOpen: boolean;
  isLoading: boolean;
  showPredictions: boolean;
  predictions: T[];
  placeholder: string;
  onRequestClose: () => void;
  onChange: (query: string) => void;
  onClickPrediction: (prediction: T) => void;
}) {
  const [parent] = useAutoAnimate<HTMLDivElement>();
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (inputRef.current !== null) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <Root open={props.isOpen}>
      <Dialog.Portal>
        <Content ref={parent}>
          <Header>
            <Input
              ref={inputRef}
              value={props.query}
              placeholder={props.placeholder}
              required={true}
              autoFocus={true}
              autoComplete="off"
              disabled={props.isLoading}
              onChange={(event) => props.onChange(event.target.value)}
            />

            {!props.isLoading ? (
              <Dialog.Close asChild={true}>
                <CloseIcon size={32} icon={TIMES_SVG} onClick={props.onRequestClose} />
              </Dialog.Close>
            ) : (
              <Dialog.Close asChild={true}>
                <Spinner size={32} onClick={props.onRequestClose} />
              </Dialog.Close>
            )}
          </Header>
          <FlexPadding direction="column">
            {!props.isLoading && props.showPredictions
              ? props.predictions.map((prediction) => (
                  <AutoCompletePrediction
                    key={prediction.id}
                    prediction={prediction}
                    query={props.query}
                    onClick={props.onClickPrediction}
                  />
                ))
              : null}
          </FlexPadding>
        </Content>
      </Dialog.Portal>
    </Root>
  );
}

const FlexPadding = styled(Flex)`
  padding-top: 80px;
`;

const dialogAnimation = keyframes`
    from {
        opacity: 0;
        transform: scale(1.2);
    }
`;

const Root = styled(Dialog.Root)``;

const CloseIcon = styled(AppIcon)``;

const Header = styled.div`
  display: flex;
  align-items: center;
  position: fixed;
  top: 0;
  width: 100%;
  background-color: white;
  z-index: 1;
  border-bottom: 1px solid var(--gray-100);
  margin-top: env(safe-area-inset-top);

  ${CloseIcon}, ${Spinner.Styled} {
    padding: 16px;
  }
`;

const Input = styled.input`
  border: none;
  font-family: inherit;
  width: 100%;
  padding: 24px;
  font-size: 18px;
  font-weight: 600;
`;

const Content = styled(Dialog.Content)`
  animation: ${dialogAnimation} 0.2s ease-out;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  overflow-y: auto;
  background-color: #fff;

  ${Question.Input.Styled} {
    margin: 16px;
  }

  ${AutoCompletePrediction.Root} {
    padding: 16px 24px;

    + ${AutoCompletePrediction.Root} {
      border-top: 1px var(--gray-100) solid;
    }
  }
`;

AutoCompleteDialog.Root = Root;
AutoCompleteDialog.Content = Content;
AutoCompleteDialog.Header = Header;
AutoCompleteDialog.Input = Input;
AutoCompleteDialog.CloseIcon = CloseIcon;

export default AutoCompleteDialog;
